import { ActionListItem, ActionListBusyIndicator } from "client/Components";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EventCommentNotificationDataViewModel } from "client/Api/Notifications/notifications";
import { EventStatisticsModel, getStatistics } from "client/Api/Hub/Events/eventViewer";
import { useFollowAction } from "client/Features/Hub/Events/Details/EventActions/useFollowAction";

type LoadingResult =
	{
		wasSuccessful: false;
	}
	|
	{
		wasSuccessful: true;
		isFollowing: boolean;
	};

export type EventCommentActionsProps = {
	data: EventCommentNotificationDataViewModel;
};

export const EventCommentActions = (props: EventCommentActionsProps): ReactElement | null => {
	const {
		data
	} = props;

	const { eventId } = data;

	const [loadingResult, setLoadingResult] = useState<LoadingResult | null>(null);

	useEffect(
		(): (() => void) | void => {
			const load = async (eventId: number, signal: AbortSignal): Promise<void> => {
				try {
					const { isCurrentUserFollowingComments } = await getStatistics(eventId, signal);
					setLoadingResult({
						wasSuccessful: true,
						isFollowing: isCurrentUserFollowingComments
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setLoadingResult({
						wasSuccessful: false
					});
				}
			};

			const cancellation = new AbortController();
			load(eventId, cancellation.signal);
			return () => cancellation.abort();
		},
		[eventId]
	);

	const isFollowingChangeHandler = useCallback(
		(isFollowing: boolean): void => {
			setLoadingResult({
				wasSuccessful: true,
				isFollowing
			});
		},
		[]
	);

	if (loadingResult === null) {
		return (
			<ActionListBusyIndicator />
		);
	}

	if (!loadingResult.wasSuccessful) {
		return null;
	}

	return (
		<FollowActionListItem
			eventId={eventId}
			isFollowing={loadingResult.isFollowing}
			onIsFollowingChanged={isFollowingChangeHandler}
		/>
	);
};
EventCommentActions.displayName = "EventCommentActions";

type FollowActionListItemProps = {
	eventId: number;
	isFollowing: boolean;
	onIsFollowingChanged: (isFollowing: boolean) => void;
};

const FollowActionListItem = (props: FollowActionListItemProps): ReactElement => {
	const {
		eventId,
		isFollowing,
		onIsFollowingChanged
	} = props;

	const statisticsChangeHandler = useCallback(
		(statistics: EventStatisticsModel): void => {
			onIsFollowingChanged(statistics.isCurrentUserFollowingComments);
		},
		[onIsFollowingChanged]
	);

	const [icon, key, clickHandler] = useFollowAction(
		eventId,
		isFollowing,
		statisticsChangeHandler
	);

	return (
		<ActionListItem
			icon={icon}
			text={localize(key)}
			onClick={clickHandler}
			trackingId={isFollowing ? "unfollow" : "follow"}
		/>
	);
};
FollowActionListItem.displayName = "FollowActionListItem";